import { FC } from 'react';
import styled from 'styled-components';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import SplitIOTreatment from '../../../components/splitio/splitioTreatment';
import { Link } from '../../types';

import { getTourViewed, setTourViewed, getTour } from '../../services/TourService';
import ReactJoyride from 'react-joyride';
import { mixPanelNavHandler } from '../../services/MixPanel';
import mixpanel from 'mixpanel-browser';

const Container = styled.nav`
  display: none;

  @media screen and (min-width: 1200px) {
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
  }
`;

const StyledNavLink = styled(NavLink)(({ theme }) => {
  const { color, type } = theme;

  return `
    font-size: ${type.size.regular};
    font-weight: ${type.weight.bold};
    font-family: ${type.face};
    margin: 0 1rem;
    display: inline-flex;
    flex-flow: row nowrap;
    align-items: center;
    position: relative;

    @media screen and (min-width: 1280px) {
      margin: 0 1.75rem;
      font-size: 1.125rem;
    }

    &:hover,
    &.active {
      text-decoration: none;
      color: ${color.primary.regular};
    }

    &.active::after {
      content: '';
      display: block;
      background-color: ${color.primary.regular};
      height: 2px;
      width: 32px;
      border-radius: 9px;
      position: absolute;
      left: 50%;
      top: 100%;
      transform: translateX(-50%);
    }

    &.playLink.active::after {
      margin-left: -1rem;
    }
  `;
});

const Icon = styled.span`
  display: inline-block;
  width: 24px;
  height: 24px;
  margin-left: 0.6875rem;

  svg {
    width: 8px;
  }
`;

export interface IMainNav {
  links: Link[];
}

const MainNav: FC<IMainNav> = ({ links }) => {
  const location = useLocation();

  const handleTours = () => {
    return links.map((link) => {
      if (link.tour && !getTourViewed(link.tour)) {
        const tour = getTour(link.tour);
        if (typeof link.featureToggle !== 'undefined') {
          return (
            <SplitIOTreatment featureName={link.featureToggle.name} trafficType={link.featureToggle.type}>
              {(treatment: string) => {
                if (treatment === 'on') {
                  setTourViewed(link.tour as string);
                  return (
                    <ReactJoyride
                      steps={tour.steps}
                      continuous={true}
                      showSkipButton={true}
                      disableOverlay={false}
                      disableScrolling={true}
                      locale={tour.locale ? tour.locale : {}}
                    ></ReactJoyride>
                  );
                } else {
                  return null;
                }
              }}
            </SplitIOTreatment>
          );
        } else {
          return (
            <ReactJoyride
              steps={tour.steps}
              continuous={true}
              showSkipButton={true}
              disableOverlay={false}
              disableScrolling={true}
              locale={tour.locale ? tour.locale : {}}
            ></ReactJoyride>
          );
        }
      }
    });
  };

  return (
    <Container>
      {links.map((link) => {
        if (link.to) {
          const isPlayLink = link.to === '/play';
          const treatmentValue = link.featureToggle?.treatmentValue || false;

          let className = isPlayLink ? 'playLink' : '';

          if (link.tour) {
            className += ' ' + link.tour;
          }

          if (typeof link.featureToggle !== 'undefined') {
            return (
              <SplitIOTreatment featureName={link.featureToggle.name} trafficType={link.featureToggle.type}>
                {(treatment: string) => {
                  if (link.to && ((!treatmentValue && treatment === 'on') || treatmentValue === treatment)) {
                    return (
                      <StyledNavLink
                        exact
                        key={`mainNav-${link.to.replace(/^\w+$/, '')}`}
                        className={className}
                        to={isPlayLink ? { pathname: `/play`, state: { from: location.pathname } } : link.to}
                        onClick={() => {
                          if (link.mixPanelLogClick) {
                            const e = link.mixPanelLogClick;
                            mixPanelNavHandler(e.platform, e.moduleName, e.eventType, e.pageName, e.selection);
                          }

                          if (link.mixPanelEvent) {
                            mixpanel.track(link.mixPanelEvent.name, link.mixPanelEvent.data);
                          }
                        }}
                      >
                        {link.label}

                        {isPlayLink && (
                          <Icon>
                            <FontAwesomeIcon icon={faChevronRight} />
                          </Icon>
                        )}
                      </StyledNavLink>
                    );
                  } else {
                    return null;
                  }
                }}
              </SplitIOTreatment>
            );
          }

          return (
            <StyledNavLink
              exact
              key={`mainNav-${link.to.replace(/^\w+$/, '')}`}
              className={className}
              to={isPlayLink ? { pathname: `/play`, state: { from: location.pathname } } : link.to}
              onClick={() => {
                if (link.mixPanelLogClick) {
                  const e = link.mixPanelLogClick;
                  mixPanelNavHandler(e.platform, e.moduleName, e.eventType, e.pageName, e.selection);
                }

                if (link.mixPanelEvent) {
                  mixpanel.track(link.mixPanelEvent.name, link.mixPanelEvent.data);
                }
              }}
            >
              {link.label}
              {isPlayLink && (
                <Icon>
                  <FontAwesomeIcon icon={faChevronRight} />
                </Icon>
              )}
            </StyledNavLink>
          );
        }
      })}

      {handleTours()}
    </Container>
  );
};

export default MainNav;
